import { UIRouterGlobals } from "@uirouter/angular";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormGroup } from "@angular/forms";
import matches from "lodash.matches";
import pick from "lodash.pick";
import { BehaviorSubject, Observable, debounceTime, map, merge, take, takeUntil } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IGoal } from "@gtmhub/goals/models";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IReduxTeam, ITeamsStoreState } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { FormFields } from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.component";
import { FocusField, MetricFormParams, PiSuggestionsType } from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.models";
import { generateDefaultMetricValues, normalizeFormValues } from "@webapp/okrs/metrics/components/create-metric-form/create-metric-form.utils";
import { CreateKrSuggestionDrawerNoSuggestionReasonsMap } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/create-metric-suggestion-drawer/models/create-metric-suggestion-drawer.models";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import {
  generateQuantivePlusKeyResultDescriptionSuggestionPayload,
  generateQuantivePlusSuggestionsPayload,
} from "@webapp/platform-intelligence/quantive-plus/utils/utils";
import { PIImproveSuggestionContext } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/improvement/models";
import { IQuantivePlusMetric } from "@webapp/platform-intelligence/shared/models";
import { PIKeyResultDescriptionSuggestion, PIKrSuggestion } from "@webapp/platform-intelligence/shared/models/quantive-plus-suggestions.models";
import { QuantivePlusSuggestionsFacade } from "@webapp/platform-intelligence/shared/services/quantive-plus-suggestions/quantive-plus-suggestions-facade.service";
import { mapMetricsToPiExistingKrs } from "@webapp/platform-intelligence/shared/utils/pi-adapter.utils";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { PiTrackingEventsEnum, PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { SuggestionDrawerSectionType, SuggestionDrawerSuggestionGroupType } from "../shared/models/suggestion-drawer.models";

@Component({
  selector: "pi-create-metric-suggestion-drawer",
  templateUrl: "./create-metric-suggestion-drawer.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
})
export class CreateMetricSuggestionDrawerComponent implements OnInit, OnChanges {
  @Input() public isOpen = false;
  @Input() public statePropagator: SuggestionDrawerIndicatorStatePropagator;
  @Input() public visibilityChange: (isVisible: boolean) => void;
  @Input() public metricForm: FormGroup<FormFields>;
  @Input() public goal: IGoal;
  @Input() public flowId: string;
  @Input() public hasAcceptedTitleSuggestion: boolean;
  @Input() public hasAcceptedDescriptionSuggestion: boolean;

  @Output() private readonly suggestions: EventEmitter<PiSuggestionsType> = new EventEmitter();
  @Output() private readonly focusField: EventEmitter<FocusField> = new EventEmitter();

  public flowName: PiTrackingFlowNameEnum;
  public titleSection = new BehaviorSubject<SuggestionDrawerSectionType<{ title: { isTitleAvailable: boolean } } & SuggestionDrawerSuggestionGroupType>>(null);
  public descriptionSection = new BehaviorSubject<
    SuggestionDrawerSectionType<{ description: { isDescriptionAvailable: boolean } } & SuggestionDrawerSuggestionGroupType>
  >(null);
  public assigneesIdMap: Record<string, Assignee>;
  public teams: IReduxTeam[];

  private stateParams: MetricFormParams;
  private hasSentFeedback: boolean;
  private suggestionDrawerCurrentTitleSectionSuggestion: IQuantivePlusMetric;
  private suggestionDrawerCurrentDescriptionSectionSuggestion: Pick<IQuantivePlusMetric, "description">;
  private suggestedMetricFieldsNotVisibleInUI: Pick<IQuantivePlusMetric, "initialValue" | "target" | "targetOperator" | "format">;
  private suggestionIndicatorFakeLoadingTimeout: number;
  private suggestedMetricPropsWithoutSuggestionCardEquivalentInUI: (keyof IQuantivePlusMetric)[] = ["initialValue", "target", "targetOperator", "format"];

  constructor(
    private quantivePlusSuggestionsFacade: QuantivePlusSuggestionsFacade,
    private analyticsService: AnalyticsService,
    private uIRouterGlobals: UIRouterGlobals,
    private destroyService: DestroyService,
    private broadcastService: BroadcastService,
    private cdr: ChangeDetectorRef
  ) {
    this.stateParams = this.uIRouterGlobals.params as unknown as MetricFormParams;
    this.flowName = this.stateParams.flowName;
    this.setSuggestionDrawerInitialValues();
  }

  public ngOnInit(): void {
    this.loadRedux$().subscribe(() => {
      if (this.stateParams.id) {
        this.setSuggestionDrawerSectionsImproveSuggestionContext();
      }

      this.subscribeToMetricFormChanges();

      this.setSuggestionIndicatorFakeLoadingState();

      merge(this.titleSection, this.descriptionSection)
        .pipe(takeUntil(this.destroyService))
        .subscribe(() => this.onSectionUpdate());
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.isOpen && changes.isOpen.currentValue) {
      this.initiatePiFlowIfNeeded();
      this.reflectChangesInAllSuggestionDrawerSections();
    }
  }

  private loadRedux$(): Observable<void> {
    const { reduxStore } = reduxStoreContainer;
    const reduxObserver = new ReduxStoreObserver(reduxStore);
    return reduxObserver
      .whenFetched$<IAssigneesStoreState & ITeamsStoreState>("assignees", "teams")
      .pipe(takeUntil(this.destroyService))
      .pipe(
        map(({ assignees, teams }) => {
          this.assigneesIdMap = assignees.map;
          this.teams = teams.items;
        })
      );
  }

  private onSectionUpdate(): void {
    if (this.statePropagator && this.titleSection.value && this.descriptionSection.value) {
      const isLoading = this.titleSection.value.isLoading || this.descriptionSection.value.isLoading;
      const suggestionStateLevel = undefined;
      const isSuggestionAvailable = this.titleSection.value.title.isTitleAvailable || this.descriptionSection.value.description.isDescriptionAvailable;
      // When creating KR, we rely entirely on the information coming from the parent Objective to generate suggestions.
      const hasRequiredDependencies = true;

      this.statePropagator.emitStateChange({ isLoading, suggestionStateLevel, hasRequiredDependencies, isSuggestionAvailable });
    }
  }

  private subscribeToMetricFormChanges(): void {
    this.metricForm.controls.name.valueChanges.pipe(takeUntil(this.destroyService), debounceTime(1000)).subscribe((name) => {
      if (!name) {
        this.reflectChangesInAllSuggestionDrawerSections();
        return;
      }

      this.reflectChangesInSuggestionDrawerTitleSection();
      this.reflectChangesInSuggestionDrawerDescriptionSection();
    });

    this.metricForm.controls.description.valueChanges.pipe(takeUntil(this.destroyService), debounceTime(1000)).subscribe(() => {
      this.reflectChangesInSuggestionDrawerDescriptionSection();
    });
  }

  public setSuggestionDrawerInitialValues(): void {
    this.titleSection.next({
      isLoading: false,
      hasError: false,
      isHidden: false,
      errorData: getPiErrorData("title", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [{ id: "", suggestionText: "" }],
      title: {
        isTitleAvailable: true,
      },
      improveSuggestionContext: {
        parentId: "",
        parentType: "",
        entityId: "",
      },
      onAcceptSuggestion: (suggestion: { id: string; suggestionText: string }) => this.onTitleSuggestionAccepted(suggestion),
      onSuggestAnother: () => this.getTitleSuggestion({ isRefresh: true }),
      onRetry: () => this.getTitleSuggestion(),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableTitleSuggestion(),
    });

    this.descriptionSection.next({
      isLoading: false,
      hasError: false,
      isHidden: false,
      errorData: getPiErrorData("description", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [{ id: "", suggestionText: "" }],
      description: {
        isDescriptionAvailable: true,
      },
      improveSuggestionContext: {
        parentId: "",
        parentType: "",
        entityId: "",
      },
      onAcceptSuggestion: () => this.setAcceptedDescriptionSuggestion(),
      onSuggestAnother: () => this.getDescriptionSuggestion({ isRefresh: true }),
      onRetry: () => this.getDescriptionSuggestion(),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableDescriptionSuggestion(),
    });
  }

  public setSuggestionDrawerSectionsImproveSuggestionContext(): void {
    const goalId = this.stateParams.id;

    if (!goalId) {
      return;
    }

    const metricId = this.stateParams.metricId;

    const improveSuggestionContext: PIImproveSuggestionContext = {
      entityId: metricId,
      parentId: goalId,
      parentType: "objective",
    };

    this.titleSection.next({
      ...this.titleSection.value,
      improveSuggestionContext,
    });

    this.descriptionSection.next({
      ...this.descriptionSection.value,
      improveSuggestionContext,
    });
  }

  private updateSuggestionDrawerTitleSectionBasedOnDependencies(): void {
    const isTitleFilledIn = !!this.metricForm.controls.name.value;

    if (isTitleFilledIn) {
      this.titleSection.next({
        ...this.titleSection.value,
        hasAllDependencies: false,
        isLoading: false,
        noSuggestionReason: CreateKrSuggestionDrawerNoSuggestionReasonsMap.YOUR_KEY_RESULT_ALREADY_HAS_A_TITLE,
        title: { isTitleAvailable: false },
      });
    } else {
      this.titleSection.next({
        ...this.titleSection.value,
        hasAllDependencies: true,
        title: { isTitleAvailable: true },
      });
    }
  }

  public reflectChangesInSuggestionDrawerTitleSection(): void {
    const name = this.metricForm.controls.name.value;

    this.updateSuggestionDrawerTitleSectionBasedOnDependencies();

    if (!this.isOpen) {
      this.setSuggestionIndicatorFakeLoadingState();

      return;
    }

    const isTitleFilledIn = !!name;
    if (isTitleFilledIn) return;

    this.getTitleSuggestion();
  }

  public reflectChangesInAllSuggestionDrawerSections(): void {
    const name = this.metricForm.controls.name.value;
    const description = this.metricForm.controls.description.value;

    this.updateSuggestionDrawerTitleSectionBasedOnDependencies();
    this.updateSuggestionDrawerDescriptionSectionBasedOnDependencies();

    if (!this.isOpen) {
      this.setSuggestionIndicatorFakeLoadingState();

      return;
    }

    const isTitleFilledIn = !!name;
    const isDescriptionFilledIn = !!description;

    if (isTitleFilledIn && isDescriptionFilledIn) return;

    if (isTitleFilledIn) {
      this.getDescriptionSuggestion({ isRefresh: false });

      return;
    }

    this.getTitleSuggestion();
  }

  private updateSuggestionDrawerDescriptionSectionBasedOnDependencies(): void {
    const isDescriptionFilledIn = !!this.metricForm.controls.description.value;
    const isTitleFilledIn = !!this.metricForm.controls.name.value;

    if (isDescriptionFilledIn) {
      this.descriptionSection.next({
        ...this.descriptionSection.value,
        hasAllDependencies: false,
        isLoading: false,
        noSuggestionReason: CreateKrSuggestionDrawerNoSuggestionReasonsMap.YOUR_KEY_RESULT_ALREADY_HAS_A_DESCRIPTION,
        description: { isDescriptionAvailable: false },
      });
    } else if (!isTitleFilledIn) {
      this.descriptionSection.next({
        ...this.descriptionSection.value,
        hasAllDependencies: false,
        isLoading: false,
        noSuggestionReason: CreateKrSuggestionDrawerNoSuggestionReasonsMap.ENTER_TITLE_TO_VIEW_SUGGESTIONS,
        description: { isDescriptionAvailable: false },
      });
    } else {
      this.descriptionSection.next({
        ...this.descriptionSection.value,
        hasAllDependencies: true,
        description: { isDescriptionAvailable: true },
      });
    }
  }

  public reflectChangesInSuggestionDrawerDescriptionSection(): void {
    this.updateSuggestionDrawerDescriptionSectionBasedOnDependencies();

    if (!this.isOpen) {
      this.setSuggestionIndicatorFakeLoadingState();

      return;
    }

    const isDescriptionFilledIn = !!this.metricForm.controls.description.value;
    if (isDescriptionFilledIn) return;

    this.getDescriptionSuggestion();
  }

  public setSuggestionIndicatorFakeLoadingState(): void {
    this.titleSection.next({
      ...this.titleSection.value,
      isLoading: true,
    });

    this.descriptionSection.next({
      ...this.descriptionSection.value,
      isLoading: true,
    });

    this.cdr.detectChanges();

    const suggestionIndicatorFakeLoadingDelay = 2000;

    this.suggestionIndicatorFakeLoadingTimeout = window.setTimeoutOutsideAngular(() => {
      window.clearTimeoutOutsideAngular(this.suggestionIndicatorFakeLoadingTimeout);
      return setTimeout(() => {
        if (!this.isOpen) {
          this.titleSection.next({
            ...this.titleSection.value,
            isLoading: false,
          });
          this.descriptionSection.next({
            ...this.descriptionSection.value,
            isLoading: false,
          });

          this.cdr.detectChanges();
        }
      }, suggestionIndicatorFakeLoadingDelay);
    });
  }

  private getTitleSuggestion(args: { isRefresh?: boolean } = {}): void {
    this.titleSection.next({
      ...this.titleSection.value,
      isLoading: true,
      hasError: false,
    });

    this.getKrSuggestion({ isRefresh: args.isRefresh, rejectedKrs: [this.suggestionDrawerCurrentTitleSectionSuggestion] })
      .pipe(take(1))
      .subscribe({
        next: (suggestion) => {
          this.suggestedMetricFieldsNotVisibleInUI = pick(suggestion.suggestions.keyresults[0], this.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI);

          this.titleSection.next({
            ...this.titleSection.value,
            isLoading: false,
            hasError: false,
            suggestions: [
              {
                id: suggestion.suggestions.keyresults[0].id,
                suggestionText: suggestion.suggestions.keyresults[0].name,
              },
            ],
          });

          this.suggestionDrawerCurrentTitleSectionSuggestion = suggestion.suggestions.keyresults[0];

          this.cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.titleSection.next({
            ...this.titleSection.value,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          });

          this.cdr.detectChanges();
        },
      });
  }

  private getDescriptionSuggestion(args: { isRefresh?: boolean } = {}): void {
    this.descriptionSection.next({
      ...this.descriptionSection.value,
      isLoading: true,
      hasError: false,
    });

    this.getKrDescriptionSuggestion({ isRefresh: args.isRefresh, rejectedKrDescriptionSuggestion: [this.suggestionDrawerCurrentDescriptionSectionSuggestion] })
      .pipe(take(1))
      .subscribe({
        next: (suggestion) => {
          this.descriptionSection.next({
            ...this.descriptionSection.value,
            isLoading: false,
            hasError: false,
            suggestions: [
              {
                id: suggestion.suggestions.id,
                suggestionText: suggestion.suggestions.description,
              },
            ],
          });

          this.suggestionDrawerCurrentDescriptionSectionSuggestion = { description: suggestion.suggestions.description };

          this.cdr.detectChanges();
        },
        error: (err: HttpErrorResponse) => {
          this.descriptionSection.next({
            ...this.descriptionSection.value,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("description", err),
          });

          this.cdr.detectChanges();
        },
      });
  }

  private getKrSuggestion(params: { isRefresh: boolean; rejectedKrs?: IQuantivePlusMetric[] }): Observable<PIKrSuggestion> {
    const metrics = [...(this.goal.metrics || [])];
    const existingKrs = mapMetricsToPiExistingKrs(metrics);

    const payload = generateQuantivePlusSuggestionsPayload({
      subEntityType: "createMetricTitle",
      goal: this.goal,
      assigneesMap: this.assigneesIdMap,
      existingKrs: existingKrs,
      teams: this.teams,
      nSuggestions: 1,
      ...(params.isRefresh && {
        isRefresh: true,
        rejectedKrs: params.rejectedKrs,
      }),
    });

    return this.quantivePlusSuggestionsFacade.getQuantivePlusKrSuggestion({ ...payload, flowId: this.flowId });
  }

  private getKrDescriptionSuggestion(params: {
    isRefresh?: boolean;
    rejectedKrDescriptionSuggestion?: Pick<IQuantivePlusMetric, "description">[];
  }): Observable<PIKeyResultDescriptionSuggestion> {
    const payload = generateQuantivePlusKeyResultDescriptionSuggestionPayload({
      entityId: this.goal.id,
      subEntityType: "createMetricDescription",
      metric: normalizeFormValues(this.metricForm.getRawValue()),
      assigneesMap: this.assigneesIdMap,
      teams: this.teams,
      ...(params.isRefresh && {
        isRefresh: true,
        rejectedDescriptions: params.rejectedKrDescriptionSuggestion,
      }),
    });

    return this.quantivePlusSuggestionsFacade.getKeyResultDescriptionSuggestion({ ...payload, flowId: this.flowId });
  }

  private onTitleSuggestionAccepted(suggestion: { id: string; suggestionText: string }): void {
    this.setAcceptedTitleSuggestion(suggestion);
    this.applySuggestedFieldValuesNotAvailableInUI();
    this.reflectChangesInSuggestionDrawerDescriptionSection();
    this.trackPiSuggestionAccepted(this.titleSection.value.suggestions[0].id);
  }

  private setAcceptedTitleSuggestion(suggestion: { id: string; suggestionText: string }): void {
    this.suggestions.emit({
      name: suggestion.suggestionText,
    });

    this.focusField.emit("shouldFocusTitleInput");

    this.reflectChangesInSuggestionDrawerTitleSection();

    /** fetch relevant insights when we dynamically change metric name */
    this.broadcastService.emit("searchInsightSuggestions", { name: suggestion.suggestionText });
  }

  private showLastAvailableTitleSuggestion(): void {
    this.titleSection.next({
      ...this.titleSection.value,
      hasError: false,
      errorData: getPiErrorData("title", null),
    });
  }

  private applySuggestedFieldValuesNotAvailableInUI(): void {
    const actualMetricValuesForSuggestedKeys = pick(normalizeFormValues(this.metricForm.getRawValue()), this.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI);
    const defaultMetricValuesForSuggestedKeys = pick(generateDefaultMetricValues(null, null), this.suggestedMetricPropsWithoutSuggestionCardEquivalentInUI);
    const shouldSkipSuggestedValues = !matches(actualMetricValuesForSuggestedKeys)(defaultMetricValuesForSuggestedKeys);

    if (shouldSkipSuggestedValues) return;

    this.suggestions.emit({
      targetOperator: this.suggestedMetricFieldsNotVisibleInUI.targetOperator,
      format: this.suggestedMetricFieldsNotVisibleInUI.format,
      target: this.suggestedMetricFieldsNotVisibleInUI.target,
      initialValue: this.suggestedMetricFieldsNotVisibleInUI.initialValue,
    });
  }

  private setAcceptedDescriptionSuggestion(): void {
    this.suggestions.emit({
      description: this.descriptionSection.value.suggestions[0].suggestionText,
    });

    this.focusField.emit("shouldFocusDescriptionInput");

    this.reflectChangesInSuggestionDrawerDescriptionSection();
    this.trackPiSuggestionAccepted(this.descriptionSection.value.suggestions[0].id);
  }

  private showLastAvailableDescriptionSuggestion(): void {
    this.descriptionSection.next({
      ...this.descriptionSection.value,
      hasError: false,
      errorData: getPiErrorData("description", null),
    });
  }

  private initiatePiFlowIfNeeded(): void {
    /** we only send the flow initiated event once */
    if (this.hasSentFeedback) return;

    this.hasSentFeedback = true;
    this.analyticsService.track(PiTrackingEventsEnum.PiFlowInitiated, { flowName: this.flowName, flowId: this.flowId, userId: getCurrentUserId() });
  }

  private trackPiSuggestionAccepted(suggestionId: string): void {
    const hasAcceptedAnySuggestions = this.hasAcceptedTitleSuggestion || this.hasAcceptedDescriptionSuggestion;
    if (!this.flowId || !hasAcceptedAnySuggestions) return;

    this.analyticsService.track(PiTrackingEventsEnum.PiSuggestionAccepted, {
      flowId: this.flowId,
      flowName: this.flowName,
      userId: getCurrentUserId(),
      suggestionId,
    });
  }
}
